import { midnightMarketsByChain } from '@1delta/data-sdk'
import { createMidnightBookSource } from './apiClient'
import type {
  MidnightBook,
  MidnightBookSource,
  MidnightBookTop,
  MidnightMarketRaw,
} from './types'

/** Levels kept per book side in the public batch (a chunk for later filtering). */
const MIDNIGHT_BOOK_LEVELS = 20

/**
 * Last-known-good snapshot cache, keyed by `${chainId}:${marketId}`.
 *
 * The Midnight book API is rate-limited and occasionally 403s. A raw failure
 * would collapse a market's rate to 0 AND drop its `terms` (see convertPublic)
 * for the entire downstream meta-cache window (~5 min at the edge) — blanking
 * the public "Fixed from X%" display and zeroing the user-data borrow-APR
 * baseline. Reusing the last good snapshot on a transient failure keeps a
 * (slightly stale but representative) rate + book flowing until the API recovers.
 *
 * Safe because the meta rate/book are DISPLAY-ONLY for Midnight: leverage/loop
 * builders price off live quotes (`fetchMidnightQuote`), never this snapshot. The
 * window is bounded so a genuinely dead market eventually falls back to 0.
 */
const LKG_TTL_SEC = 30 * 60
const lastGood = new Map<
  string,
  { top: MidnightBookTop; book: MidnightBook | null; at: number }
>()

async function fetchTopAndBookWithFallback(
  source: MidnightBookSource,
  chainId: string,
  marketId: string,
  nowSec: number,
): Promise<{ top: MidnightBookTop | null; book: MidnightBook | null }> {
  const key = `${chainId}:${marketId}`

  // Prefer the one-fetch top+book (same /books call); fall back to top-only for
  // stub sources that don't implement `getTopAndBook`.
  let fresh: { top: MidnightBookTop; book: MidnightBook } | null = null
  if (source.getTopAndBook) {
    fresh = await source
      .getTopAndBook(marketId, MIDNIGHT_BOOK_LEVELS)
      .catch(() => null)
  } else {
    const top = await source.getBookTop(marketId).catch(() => null)
    if (top) fresh = { top, book: { bids: [], asks: [] } }
  }

  if (fresh) {
    lastGood.set(key, { top: fresh.top, book: fresh.book, at: nowSec })
    return { top: fresh.top, book: fresh.book }
  }
  const cached = lastGood.get(key)
  if (cached && nowSec - cached.at <= LKG_TTL_SEC) {
    return { top: cached.top, book: cached.book }
  }
  return { top: null, book: null }
}

/**
 * Fetch the current top-of-book + a bounded book chunk for every configured
 * Midnight market on a chain.
 *
 * Public data = static market config (from `midnightMarkets` registry) + live
 * order-book rates/depth (from the book source). No RPC is required for the
 * public view: LLTVs / maturities / decimals come from config, rates from the
 * API. The aggregate best rate + FULL depth live on `top`; `book` carries the
 * best `MIDNIGHT_BOOK_LEVELS` levels per side for downstream filtering. Both are
 * null only when the book fetch failed AND no recent snapshot is cached (the
 * converter still emits the market with zeroed rates in that case).
 */
export async function fetchMidnightMarkets(
  chainId: string,
  source: MidnightBookSource = createMidnightBookSource(chainId),
): Promise<MidnightMarketRaw[]> {
  const markets = midnightMarketsByChain(chainId)
  if (markets.length === 0) return []

  const nowSec = Math.floor(Date.now() / 1000)
  return Promise.all(
    markets.map(async (config) => {
      // Matured markets are emitted with zeroed rates/depth by the converter, so
      // the book fetch is wasted — skip the upstream call for them.
      if (Number(config.maturity) <= nowSec) {
        return { config, top: null, book: null }
      }
      const { top, book } = await fetchTopAndBookWithFallback(
        source,
        chainId,
        config.marketId,
        nowSec,
      )
      return { config, top, book }
    }),
  )
}
